import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import AddWorkspaceModal from './AddWorkspaceModal';
import { getHubSpotContext } from '../utils/hubspotContext';
import './WorkspaceSwitcher.css';

function WorkspaceSwitcher({ currentWorkspace, onWorkspaceChange, token }) {
  const [workspaces, setWorkspaces] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (token) {
      fetchWorkspaces();
    }
  }, [token]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const fetchWorkspaces = async () => {
    try {
      const response = await api.fetchWorkspaces(token);
      setWorkspaces(response.data.workspaces || []);
    } catch (error) {
      console.error('Error fetching workspaces:', error);
    }
  };

  const handleWorkspaceSwitch = async (workspace) => {
    if (workspace.teamId === currentWorkspace?.teamId) {
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    try {
      await api.switchWorkspace(workspace.teamId, token);

      // Store selected workspace in localStorage for persistence
      localStorage.setItem('selected_workspace_id', workspace.teamId);

      // Notify parent component to reload data
      if (onWorkspaceChange) {
        onWorkspaceChange(workspace);
      }

      setIsOpen(false);
    } catch (error) {
      console.error('Error switching workspace:', error);
      alert('Failed to switch workspace. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnectWorkspace = async (workspace, event) => {
    event.stopPropagation();

    if (workspaces.length === 1) {
      alert('Cannot disconnect the only workspace. Please connect another workspace first.');
      return;
    }

    if (!window.confirm(`Are you sure you want to disconnect from ${workspace.teamName}?`)) {
      return;
    }

    try {
      await api.disconnectWorkspace(workspace.teamId, token);

      // If we disconnected the current workspace, switch to another
      if (workspace.teamId === currentWorkspace?.teamId) {
        const remainingWorkspaces = workspaces.filter(w => w.teamId !== workspace.teamId);
        if (remainingWorkspaces.length > 0) {
          await handleWorkspaceSwitch(remainingWorkspaces[0]);
        }
      }

      // Refresh workspace list
      await fetchWorkspaces();
    } catch (error) {
      console.error('Error disconnecting workspace:', error);
      alert('Failed to disconnect workspace. Please try again.');
    }
  };

  const handleAddWorkspace = () => {
    setIsOpen(false);
    setShowAddModal(true);
  };

  const handleWorkspaceSubmit = (workspaceUrl) => {
    setShowAddModal(false);

    // Store workspace info
    localStorage.setItem('adding_workspace', 'true');
    localStorage.setItem('target_workspace', workspaceUrl);

    // Get HubSpot context (try multiple sources)
    const hubspotContext = getHubSpotContext();
    const hubspotUserId = hubspotContext?.userId || localStorage.getItem('hubspot_user_id');
    const hubspotPortalId = hubspotContext?.portalId || localStorage.getItem('hubspot_portal_id');

    if (!hubspotUserId) {
      console.error('No HubSpot user ID found! Workspace cannot be saved.');
      alert('Error: Unable to identify user. Please refresh and try again.');
      return;
    }

    // Build OAuth URL with popup=true and workspace parameter. Carry the signed
    // handoff token — the backend only trusts identity from `st`/`x-hubspot-handoff`
    // (see auth.js getVerifiedIdentity); without it this falls back to the session,
    // which isn't reliably present in a cross-site iframe context.
    const params = new URLSearchParams();
    params.set('popup', 'true'); // IMPORTANT: Open in popup!
    params.set('workspace', workspaceUrl);
    params.set('hubspotUserId', hubspotUserId);
    if (hubspotPortalId) {
      params.set('hubspotPortalId', hubspotPortalId);
    }
    if (hubspotContext?.handoffToken) {
      params.set('st', hubspotContext.handoffToken);
    }

    const oauthUrl = `/api/slack/oauth?${params.toString()}`;

    console.log('Opening OAuth popup for workspace:', workspaceUrl);

    // Open popup window for OAuth (same as first sign-in)
    const width = 600;
    const height = 700;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const authPopup = window.open(
      oauthUrl,
      'SlackAddWorkspace',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    if (!authPopup) {
      alert('Popup was blocked. Please allow popups and try again.');
      return;
    }

    // Poll to check if popup closed or auth completed
    const checkInterval = setInterval(() => {
      if (authPopup.closed) {
        clearInterval(checkInterval);
        // Refresh workspace list after popup closes
        fetchWorkspaces();
      }
    }, 1000);

    // Listen for auth success message from popup
    const messageHandler = (event) => {
      if (event.data && event.data.type === 'SLACK_AUTH_SUCCESS') {
        clearInterval(checkInterval);
        if (authPopup && !authPopup.closed) {
          authPopup.close();
        }
        window.removeEventListener('message', messageHandler);

        // Reload page to show new workspace
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    };

    window.addEventListener('message', messageHandler);
  };

  // Always show the component, even if loading
  if (!currentWorkspace && workspaces.length === 0) {
    // Show "Connect Workspace" button if nothing is loaded yet
    return (
      <div className="workspace-switcher">
        <button
          className="workspace-switcher-button"
          onClick={handleAddWorkspace}
          style={{ cursor: 'pointer' }}
        >
          <div className="workspace-info">
            <span className="workspace-name">Connect Workspace</span>
          </div>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="workspace-switcher" ref={dropdownRef}>
      <button
        className="workspace-switcher-button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
      >
        <div className="workspace-info">
          <span className="workspace-name">
            {currentWorkspace?.teamName || 'Select Workspace'}
          </span>
          {workspaces.length > 1 && (
            <span className="workspace-count">({workspaces.length})</span>
          )}
        </div>
        <svg
          className={`workspace-switcher-arrow ${isOpen ? 'open' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path
            d="M2 4L6 8L10 4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="workspace-switcher-dropdown">
          <div className="workspace-switcher-header">
            <span>Workspaces</span>
          </div>

          <div className="workspace-list">
            {workspaces.map((workspace) => (
              <div
                key={workspace.teamId}
                className={`workspace-item ${
                  workspace.teamId === currentWorkspace?.teamId ? 'active' : ''
                }`}
                onClick={() => handleWorkspaceSwitch(workspace)}
              >
                <div className="workspace-item-info">
                  <div className="workspace-item-name">
                    {workspace.teamName}
                    {workspace.isPrimary && (
                      <span className="workspace-badge">Primary</span>
                    )}
                  </div>
                  {workspace.teamDomain && (
                    <div className="workspace-item-domain">
                      {workspace.teamDomain}.slack.com
                    </div>
                  )}
                </div>

                {workspace.teamId === currentWorkspace?.teamId && (
                  <svg
                    className="workspace-check-icon"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M13 4L6 11L3 8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}

                {workspaces.length > 1 && (
                  <button
                    className="workspace-disconnect-btn"
                    onClick={(e) => handleDisconnectWorkspace(workspace, e)}
                    title="Disconnect workspace"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="workspace-switcher-footer">
            <button
              className="workspace-add-btn"
              onClick={handleAddWorkspace}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 3V13M3 8H13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <span>Add Workspace</span>
            </button>
          </div>
        </div>
      )}

      <AddWorkspaceModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleWorkspaceSubmit}
      />
    </div>
  );
}

export default WorkspaceSwitcher;
